import React, { useState, useRef, useEffect } from "react";
import { cryptiqueAnalyticsTheme as theme } from "../theme";
import EventsSelectorMenu from "./EventsSelectorMenu";

function InlineDropdown({
  value,
  options = [],
  onChange,
  disabled = false,
  widthClass = "min-w-[120px]",
}) {
  const [open, setOpen] = useState(false);
  const menuRef = useRef(null);
  const buttonRef = useRef(null);

  const selected = options.find((o) => String(o.value) === String(value)) || options[0];

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (!open) return;
      if (menuRef.current && menuRef.current.contains(e.target)) return;
      if (buttonRef.current && buttonRef.current.contains(e.target)) return;
      setOpen(false);
    };
    const handleEsc = (e) => {
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("mousedown", handleClickOutside);
    document.addEventListener("keydown", handleEsc);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      document.removeEventListener("keydown", handleEsc);
    };
  }, [open]);

  return (
    <div className={`relative ${widthClass}`}>
      <button
        ref={buttonRef}
        type="button"
        disabled={disabled}
        onClick={() => !disabled && setOpen((v) => !v)}
        className="w-full h-8 px-2 text-[14px] border rounded-md flex items-center justify-between gap-2"
        style={{
          backgroundColor: "#ffffff",
          borderColor: theme.border,
          color: theme.textPrimary,
          opacity: disabled ? 0.6 : 1,
        }}
      >
        <span className="truncate text-left">{selected?.label ?? ""}</span>
        <span style={{ color: theme.textMuted }}>▾</span>
      </button>
      {open && !disabled && (
        <div
          ref={menuRef}
          className="absolute left-0 right-0 mt-1 z-[120] border rounded-lg overflow-hidden"
          style={{
            backgroundColor: theme.bgRaised,
            borderColor: theme.border,
            boxShadow: "0 10px 28px rgba(15, 23, 42, 0.16)",
            maxHeight: 220,
            overflowY: "auto",
          }}
        >
          {options.map((opt) => {
            const active = String(opt.value) === String(value);
            return (
              <button
                key={String(opt.value)}
                type="button"
                onClick={() => {
                  onChange?.(opt.value);
                  setOpen(false);
                }}
                className="w-full px-2 py-1.5 text-[13px] text-left transition-colors"
                style={{
                  backgroundColor: active ? (theme.bgOverlay || "#f1f5f9") : "transparent",
                  color: active ? (theme.textPrimary || "#0f172a") : (theme.textSecondary || "#475569"),
                  borderLeft: active ? `2px solid ${theme.primary}` : "2px solid transparent",
                }}
                onMouseEnter={(e) => {
                  if (!active) e.currentTarget.style.backgroundColor = theme.bgOverlay || "#f1f5f9";
                }}
                onMouseLeave={(e) => {
                  if (!active) e.currentTarget.style.backgroundColor = "transparent";
                }}
              >
                {opt.label}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}

/**
 * Query config for Retention: start event and return event selection.
 * Mirrors FunnelQueryConfig structure but simpler (just two events).
 */
export default function RetentionQueryConfig({
  startEvent = "",
  onStartEventChange,
  returnEvent = "",
  onReturnEventChange,
  interval = "day",
  onIntervalChange,
  retentionRule = "on_or_after",
  onRetentionRuleChange,
  metric = "retention_rate",
  onMetricChange,
  chartType = "retention-curve",
  retentionIntervals = [],
  retentionGroupIndex = 0,
  onRetentionGroupIndexChange,
  availableEvents = [],
  teamId = null,
}) {
  const [startEventMenuOpen, setStartEventMenuOpen] = useState(false);
  const [returnEventMenuOpen, setReturnEventMenuOpen] = useState(false);
  const startEventMenuRef = useRef(null);
  const returnEventMenuRef = useRef(null);
  const startEventButtonRef = useRef(null);
  const returnEventButtonRef = useRef(null);

  const eventOnlyOptions = (Array.isArray(availableEvents) ? availableEvents : []).filter(
    (evt) => (evt?.source || (evt?.type === "transaction" ? "transaction" : "event")) !== "transaction"
  );

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (startEventMenuOpen && startEventMenuRef.current && !startEventMenuRef.current.contains(e.target) && startEventButtonRef.current && !startEventButtonRef.current.contains(e.target)) {
        setStartEventMenuOpen(false);
      }
      if (returnEventMenuOpen && returnEventMenuRef.current && !returnEventMenuRef.current.contains(e.target) && returnEventButtonRef.current && !returnEventButtonRef.current.contains(e.target)) {
        setReturnEventMenuOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [startEventMenuOpen, returnEventMenuOpen]);

  const handleStartEventSelect = (name, source) => {
    onStartEventChange(name || "");
    setStartEventMenuOpen(false);
  };

  const handleReturnEventSelect = (name, source) => {
    onReturnEventChange(name || "");
    setReturnEventMenuOpen(false);
  };

  return (
    <div className="space-y-3">
      <div
        className="rounded-2xl border px-3 py-3"
        style={{ borderColor: theme.border, backgroundColor: "#f8fafc" }}
      >
        <div
          className="rounded-xl border p-3 space-y-2"
          style={{ borderColor: theme.border, backgroundColor: "#ffffff" }}
        >
          <div className="relative">
            <button
              ref={startEventButtonRef}
              type="button"
              onClick={() => setStartEventMenuOpen(!startEventMenuOpen)}
              className="w-full h-9 flex items-center justify-between px-2.5 text-[15px] border rounded-md text-left"
              style={{
                backgroundColor: "#ffffff",
                borderColor: theme.border,
                color: startEvent ? theme.textPrimary : theme.textMuted,
              }}
            >
              <span className="truncate flex-1">
                {startEvent || "Select Event"} <span style={{ color: theme.textMuted }}>then</span>
              </span>
              <span style={{ color: theme.textMuted }}>▾</span>
            </button>
            {startEventMenuOpen && (
              <div ref={startEventMenuRef} className="absolute left-0 right-0 mt-1 z-[100]">
                <EventsSelectorMenu
                  availableEvents={eventOnlyOptions}
                  onSelect={handleStartEventSelect}
                  onClose={() => setStartEventMenuOpen(false)}
                  maxHeight={180}
                  showSourceTabs={false}
                />
              </div>
            )}
          </div>

          <div className="relative">
            <button
              ref={returnEventButtonRef}
              type="button"
              onClick={() => setReturnEventMenuOpen(!returnEventMenuOpen)}
              className="w-full h-9 flex items-center justify-between px-2.5 text-[15px] border rounded-md text-left"
              style={{
                backgroundColor: "#ffffff",
                borderColor: theme.border,
                color: returnEvent ? theme.textPrimary : theme.textMuted,
              }}
            >
              <span className="truncate flex-1">{returnEvent || "Select Event"}</span>
              <span style={{ color: theme.textMuted }}>▾</span>
            </button>
            {returnEventMenuOpen && (
              <div ref={returnEventMenuRef} className="absolute left-0 right-0 mt-1 z-[100]">
                <EventsSelectorMenu
                  availableEvents={eventOnlyOptions}
                  onSelect={handleReturnEventSelect}
                  onClose={() => setReturnEventMenuOpen(false)}
                  maxHeight={180}
                  showSourceTabs={false}
                />
              </div>
            )}
          </div>

          <div className="flex items-center gap-2 pt-1 flex-wrap">
            <span className="text-[15px]" style={{ color: theme.textPrimary }}>Retention</span>
            <InlineDropdown
              value={retentionRule}
              onChange={(v) => onRetentionRuleChange(v)}
              options={[
                { value: "on", label: "On" },
                { value: "on_or_after", label: "On or After" },
                { value: "on_or_before", label: "On or Before" },
              ]}
              widthClass="min-w-[120px]"
            />
            <InlineDropdown
              value={interval}
              onChange={(v) => onIntervalChange && onIntervalChange(v)}
              options={[
                { value: "day", label: "Each Day" },
                { value: "week", label: "Each Week" },
                { value: "month", label: "Each Month" },
              ]}
              widthClass="min-w-[120px]"
            />
          </div>

          {(chartType === "line" || chartType === "metric") && (
            <div className="flex items-center gap-3 pt-1">
              <span className="text-[14px]" style={{ color: theme.textMuted }}>Retention groups</span>
              <InlineDropdown
                value={
                  retentionIntervals && retentionIntervals.length > 0
                    ? Math.min(
                        Math.max(0, retentionGroupIndex),
                        retentionIntervals.length - 1
                      )
                    : 0
                }
                onChange={(v) => onRetentionGroupIndexChange && onRetentionGroupIndexChange(Number(v))}
                options={
                  retentionIntervals && retentionIntervals.length > 0
                    ? retentionIntervals.map((label, i) => ({ value: i, label }))
                    : [{ value: 0, label: "Run query to load groups" }]
                }
                widthClass="min-w-[120px]"
              />
            </div>
          )}

          <div className="flex items-center gap-3 pt-1">
            <span className="text-[14px]" style={{ color: theme.textMuted }}>Metric</span>
            <InlineDropdown
              value={metric}
              onChange={(v) => onMetricChange(v)}
              options={[
                { value: "retention_rate", label: "Retention Rate (%)" },
                { value: "unique_users", label: "Unique Users (#)" },
              ]}
              widthClass="min-w-[140px]"
            />
          </div>
        </div>
      </div>
    </div>
  );
}
